/**
 * `coinbase` is the BTC/USD fallback used only when CoinGecko's price
 * lookup fails (its public, keyless API rate-limits/blocks shared server
 * IPs); `wallet-summary` reports both so the UI can show which one priced it.
 */
export type SourceName = 'blockstream' | 'coingecko' | 'coinbase';

export interface SourceState {
  source: SourceName;
  ok: boolean;
  stale: boolean;
  lastSuccessAt: string | null;
  error: string | null;
}

export type TransferDirection = 'in' | 'out';

export type ConfirmationState = 'confirmed' | 'pending';

export interface Transfer {
  txid: string;
  direction: TransferDirection;
  deltaSat: number;
  displayBtc: string;
  timestamp: string | null;
  confirmationState: ConfirmationState;
  blockHeight: number | null;
  /** Total inputs/outputs on the underlying transaction (not just this
   * address's side of it) — so the UI never implies a multi-party
   * transaction was a simple one-sender/one-recipient transfer. */
  vinCount: number;
  voutCount: number;
}

export interface WalletSummary {
  address: string;
  label: string;
  confirmedBalanceSat: number;
  mempoolDeltaSat: number;
  totalBalanceSat: number;
  txCount: number;
  btcUsdPrice: number | null;
  totalValueUsd: number | null;
  generatedAt: string;
  sources: SourceState[];
}

export interface WalletHistoryResponse {
  address: string;
  transfers: Transfer[];
  nextCursor: string | null;
  generatedAt: string;
  sources: SourceState[];
}

export type SignalAction =
  'BUY_SMALL' | 'SELL_REDUCE' | 'HOLD_TAKE_PROFIT' | 'HOLD_DCA_TINY' | 'HOLD';

export interface ChartPoint {
  t: number;
  price: number;
  emaFast: number | null;
  emaSlow: number | null;
}

export interface MarketSignal {
  action: SignalAction;
  label: string;
  currentPrice: number | null;
  emaFast: number | null;
  emaFastPeriod: number;
  emaSlow: number | null;
  emaSlowPeriod: number;
  rsi: number | null;
  rsiPeriod: number;
  rationale: string[];
  suggestedUsdAmount: number;
  generatedAt: string;
  disclaimer: string;
  series: ChartPoint[];
}

export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
  };
}

export const DISCLAIMER =
  'This is not financial advice, not investment advice, and not a recommendation to buy, sell, or hold any asset. Balances, prices, and signals are informational and educational only, derived from historical and technical data that can be wrong or delayed. You are solely responsible for any financial decisions and their outcomes.';
